import * as fs from 'fs';
import * as path from 'path';

import { EffectiveTree } from './effective-tree';
import { stripTsNoise } from './strip-ts-noise';
import { createIsLineDisabled } from './disable-directives';
import {
    ToolKind, NormalizedToolInput, NormalizedEdit,
    EditContext, FileContext, BashContext,
} from './types';

export class BuiltContexts {
    readonly fileContext: FileContext;
    readonly editContexts: readonly EditContext[];

    constructor(fileContext: FileContext, editContexts: readonly EditContext[]) {
        this.fileContext = fileContext;
        this.editContexts = editContexts;
    }
}

export function buildContexts(
    toolKind: ToolKind,
    input: NormalizedToolInput,
    workspaceRoot: string,
): BuiltContexts {
    const filePath = input.filePath;
    const relativePath = path.relative(workspaceRoot, filePath);
    const edits = input.edits;

    const currentFileLines = readCurrentFileLines(filePath);
    let linesAdded = 0;
    let linesRemoved = 0;
    for (const e of edits) {
        linesAdded += countLines(e.newString);
        linesRemoved += countLines(e.oldString);
    }

    const projectedFileLines =
        toolKind === 'Write'
            ? countLines(edits.length > 0 ? edits[0].newString : '')
            : currentFileLines + linesAdded - linesRemoved;

    const fileContext = new FileContext(
        toolKind,
        filePath,
        relativePath,
        workspaceRoot,
        currentFileLines,
        linesAdded,
        linesRemoved,
        projectedFileLines,
    );

    const editContexts = edits.map((e, idx) => {
        const addedContent = e.newString;
        const stripped = stripTsNoise(addedContent);
        const isLineDisabled = createIsLineDisabled(addedContent);
        return new EditContext(
            toolKind,
            idx,
            edits.length,
            filePath,
            relativePath,
            workspaceRoot,
            addedContent,
            stripped,
            addedContent.split('\n'),
            stripped.split('\n'),
            e.oldString,
            isLineDisabled,
        );
    });

    return new BuiltContexts(fileContext, editContexts);
}

// The bash context is built from the RESOLVED tree, not the shell's cwd: `tree.root` is the worktree
// the command acts on (what the guards must judge) while `tree.governedRoot` still owns the config.
// webpieces-disable no-function-outside-class -- sibling of buildContexts() above; this module is a pair of module-scope context factories and a lone class for one of them would break its shape
export function buildBashContext(command: string, tree: EffectiveTree): BashContext {
    return new BashContext(command, tree.root, tree.effectiveCwd, tree.governedRoot);
}

function readCurrentFileLines(filePath: string): number {
    // eslint-disable-next-line @webpieces/no-unmanaged-exceptions
    try {
        const content = fs.readFileSync(filePath, 'utf8');
        return countLines(content);
    } catch (err: unknown) {
        // eslint-disable-next-line @webpieces/catch-error-pattern -- file-not-found is expected for new Write targets
        void err;
        return 0;
    }
}

function countLines(s: string): number {
    if (!s) return 0;
    return s.split('\n').length;
}
